import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, Camera, Trash2, Bug, Loader2 } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useBusiness } from '../contexts/BusinessContext';
import { useAuth } from '../hooks/useAuth';
import toast from 'react-hot-toast';

interface ReportBugModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function ReportBugModal({ isOpen, onClose }: ReportBugModalProps) {
  const { user } = useAuth();
  const { businessId } = useBusiness();
  const [description, setDescription] = useState('');
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => { document.body.style.overflow = 'unset'; };
  }, [isOpen]);

  const handleTransitionEnd = () => {
    if (!isOpen) setIsAnimating(false);
  };

  const handlePhotoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image must be smaller than 10MB');
      return;
    }

    setPhotoFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setPhotoPreview(reader.result as string);
    reader.readAsDataURL(file);
  };

  const removePhoto = () => {
    setPhotoFile(null);
    setPhotoPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const resetForm = () => {
    setDescription('');
    setPhotoFile(null);
    setPhotoPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSubmit = async () => {
    if (!description.trim()) {
      toast.error('Please describe the bug');
      return;
    }

    if (!user || !supabase) {
      toast.error('You must be logged in to report a bug');
      return;
    }

    setSubmitting(true);
    try {
      let photoUrl: string | null = null;

      if (photoFile) {
        const fileExt = photoFile.name.split('.').pop();
        const fileName = `${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`;
        const filePath = `bug-reports/${user.id}/${fileName}`;

        const { error: uploadError } = await supabase.storage
          .from('user-uploads')
          .upload(filePath, photoFile, { cacheControl: '3600', upsert: false });

        if (uploadError) throw new Error(`Photo upload failed: ${uploadError.message}`);

        const { data: urlData } = supabase.storage
          .from('user-uploads')
          .getPublicUrl(filePath);

        photoUrl = urlData.publicUrl;
      }

      const { error: insertError } = await supabase
        .from('user_bugs')
        .insert({
          user_id: user.id,
          business_id: businessId || null,
          description: description.trim(),
          photo_url: photoUrl,
        });

      if (insertError) throw new Error(`Failed to submit bug report: ${insertError.message}`);

      toast.success('Bug report submitted — thank you!');
      resetForm();
      onClose();
    } catch (err) {
      console.error('Error submitting bug report:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to submit bug report');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen && !isAnimating) return null;

  return createPortal(
    <div
      className={`fixed inset-0 transition-opacity duration-200 ${
        isOpen ? 'bg-black/50' : 'bg-black/0'
      }`}
      style={{
        zIndex: 100150,
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        paddingTop: isMobile ? 'env(safe-area-inset-top, 0px)' : '0',
        paddingBottom: isMobile ? 'env(safe-area-inset-bottom, 0px)' : '0',
      }}
      onClick={onClose}
      onTransitionEnd={handleTransitionEnd}
    >
      <div
        className={`bg-white transform transition-all duration-200 ${
          isMobile
            ? `relative w-full h-full ${isOpen ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'}`
            : `fixed inset-y-0 right-0 w-full md:w-[600px] shadow-2xl ${
                isOpen ? 'translate-x-0' : 'translate-x-full'
              }`
        }`}
        style={{
          fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
          zIndex: 100160,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="h-full flex flex-col overflow-hidden">
          {/* Close Button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 h-8 w-8 flex items-center justify-center text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 active:bg-gray-200 active:scale-95 transition-all duration-150 touch-manipulation z-10"
            style={{ WebkitTapHighlightColor: 'transparent' }}
          >
            <X className="h-5 w-5" />
          </button>

          {/* Header */}
          <div className="bg-gray-50 border-b border-gray-200 px-6 py-6 text-center relative">
            <div className="flex items-center justify-center gap-2 mb-1">
              <Bug className="h-6 w-6 text-red-500" />
              <h2
                className="text-2xl font-bold text-gray-900"
                style={{ letterSpacing: '-0.02em' }}
              >
                Report a Bug
              </h2>
            </div>
            <p className="text-sm text-gray-500">
              Help us improve by describing what went wrong
            </p>
          </div>

          {/* Content */}
          <div
            className="flex-1 overflow-y-auto bg-white"
            style={{
              WebkitOverflowScrolling: 'touch',
              paddingBottom: isMobile ? 'env(safe-area-inset-bottom, 0px)' : '0',
            }}
          >
            <div className={`${isMobile ? 'p-4' : 'p-6'} space-y-6`}>
              {/* Description */}
              <div className="bg-white rounded-[12px] border border-gray-200/60 p-4 shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  What happened? <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Describe the bug in detail — what you expected to happen and what actually happened..."
                  rows={6}
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 resize-none transition-colors"
                />
              </div>

              {/* Photo Upload */}
              <div className="bg-white rounded-[12px] border border-gray-200/60 p-4 shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Screenshot (optional)
                </label>

                {photoPreview ? (
                  <div className="relative rounded-lg overflow-hidden border border-gray-200">
                    <img
                      src={photoPreview}
                      alt="Bug screenshot"
                      className="w-full max-h-64 object-contain bg-gray-50"
                    />
                    <button
                      type="button"
                      onClick={removePhoto}
                      className="absolute top-2 right-2 h-8 w-8 flex items-center justify-center bg-red-500 text-white rounded-full shadow-lg hover:bg-red-600 active:scale-95 transition-all duration-150"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="w-full flex flex-col items-center justify-center gap-2 py-8 border-2 border-dashed border-gray-300 rounded-lg text-gray-400 hover:border-blue-400 hover:text-blue-500 transition-colors duration-200"
                  >
                    <Camera className="h-8 w-8" />
                    <span className="text-sm font-medium">Tap to add a screenshot</span>
                  </button>
                )}

                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoSelect}
                  className="hidden"
                />
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="border-t border-gray-200 bg-white px-6 py-4">
            <button
              onClick={handleSubmit}
              disabled={submitting || !description.trim()}
              className="w-full py-3 px-4 bg-red-500 text-white font-semibold rounded-xl hover:bg-red-600 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-150 flex items-center justify-center gap-2"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <Bug className="h-4 w-4" />
                  Submit Bug Report
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
